import type {DriveStep, Side} from 'driver.js';

/**
 * Gibt die mobile-optimierte Popover-Position zurück.
 * Auf Desktop werden horizontale Positionen verwendet (left/right),
 * auf Mobile vertikale (top/bottom), damit das markierte Element sichtbar bleibt.
 */
const getMobileSide = (desktopSide: Side, mobileSide: Side, isMobile: boolean): Side => {
  return isMobile ? mobileSide : desktopSide;
};

/**
 * Generiert die Tour-Schritte mit dynamischer Positionierung für Mobile/Desktop.
 * @param isMobile - true wenn Bildschirmbreite < 1024px
 * @param isCommunityMember
 * @param moveNext
 */
export const getDashboardTourSteps = (isMobile: boolean, isCommunityMember: boolean, moveNext?: () => void): DriveStep[] => [
  {
    popover: {
      title: 'Willkommen beim Börsencampus! 👋',
      description: 'Damit Du das Maximale hier rausholen kannst, lass und eine kurze Tour machen, um Dir das Wichtigste zu zeigen!',
      side: 'bottom',
      align: 'start'
    }
  },
  {
    element: '[data-tour="core-card"]',
    popover: {
      title: 'Kernstrategie',
      description: 'Hier findest Du Tradingideen zur Strategie Mieteinnahmen mit Aktien! Diese bilden das Fundament und sind dazu da, wöchentlich Cashflow zu generieren!\n',
      side: getMobileSide('right', 'bottom', isMobile),
      align: 'start'
    }
  },
  {
    element: '[data-tour="earnings-card"]',
    popover: {
      title: 'Earnings Tradingideen',
      description: 'Hier findest Du spezielle Trading Gelegenheiten rund um die Quartalszahlen! Wir nutzen hohe Volatilität, um gezielt Rendite zu erzielen',
      side: getMobileSide('right', 'bottom', isMobile),
      align: 'start'
    }
  },
  {
    element: '[data-tour="swing-card"]',
    popover: {
      title: 'Swing Tradingideen',
      description: 'Kurz- bis mittelfristige Trading-Chancen mit dem Ziel von ca.10% Rendite! Hier investieren wir auf steigende, aber auch auf fallende Kurse',
      side: getMobileSide('right', 'bottom', isMobile),
      align: 'start'
    }
  },
  {
    element: '[data-tour="longterm-card"]',
    popover: {
      title: 'Langzeit-Tradingideen',
      description: 'Langfristige Qualitätsaktien oder Investments zu attraktiven Einstiegen! Für nachhaltigen Vermögensaufbau, Kinder, Enkel oder die Altersvorsorge mit der Buy& Hold Strategie',
      side: getMobileSide('right', 'bottom', isMobile),
      align: 'start'
    }
  },
  {
    element: '[data-tour="recent-activities"]',
    popover: {
      title: 'Letzte Aktivitäten',
      description: 'Bleib auf dem Laufenden! Hier siehst Du die neuesten Updates, Tradingideen und wichtige Ereignisse auf einen Blick.',
      side: getMobileSide('left', 'top', isMobile),
      align: 'start'
    }
  },
  {
    element: '[data-tour="latest-videos"]',
    popover: {
      title: 'Neueste Videos',
      description: 'Wissen ist Macht! Hier siehst Du die neusten Marktupdates und Bildungsvideos, um Deine Fähigkeiten zu verbessern',
      side: getMobileSide('left', 'top', isMobile),
      align: 'start'
    }
  },
  {
    element: '[data-tour="market-indicator"]',
    popover: {
      title: 'Börsen-Ampel',
      description: 'Die aktuelle Marktlage auf einen Blick – eingeschätzt von Sven. Ist die Ampel grün, suchen wir nach Gelegenheiten. Bei Gelb sind wir schon vorsichtiger und die Setups müssen sehr gut sein und bei rot nehmen wir Gewinne mit oder warten ab.',
      side: getMobileSide('left', 'top', isMobile),
      align: 'start'
    }
  },
  {
    element: '[data-tour="nav-signals"]',
    popover: {
      title: 'Tradingideen-Bereich',
      description: 'Hier findest Du alle detaillierten Tradingideen nach Kategorien sortiert. Klicke hier für vollständige Übersichten und Details.',
      side: 'bottom',
      align: 'start',
      onPopoverRender: () => {
        // Nur auf Mobil (< 1024px) Sidebar ausklappen
        const isMobileCheck = window.innerWidth < 1024;

        if (isMobileCheck) {
          // Mobile Hamburger-Button finden (lg:hidden = sichtbar auf Mobil)
          const mobileToggleBtn = document.querySelector('button.lg\\:hidden, button[class*="lg:hidden"]') as HTMLButtonElement;

          if (mobileToggleBtn !== null) {
            // Prüfen ob Menu geschlossen ist (Button zeigt Bars-Icon)
            const isClosed = mobileToggleBtn.querySelector('.fa-bars') !== null;

            if (isClosed) {
              // Menu öffnen
              mobileToggleBtn.click();
            }
          }
        }
      }
    }
  },
  {
    element: '[data-tour="nav-market-analysis"]',
    popover: {
      title: 'Marktanalyse',
      description: 'Tiefgehende Analysen zum aktuellen Marktgeschehen, wichtige Indikatoren und die Börsen-Ampel im Detail.',
      side: 'bottom',
      align: 'start'
    }
  },
  {
    element: '[data-tour="nav-academy"]',
    popover: {
      title: 'Trading Academy',
      description: 'Hier findest Du alles Wissen, was Du brauchst, um an der Börse erfolgreich zu werden!',
      side: 'bottom',
      align: 'start',
      ...(!isCommunityMember && moveNext !== undefined ? {
        onNextClick: () => {
          // Mobile Sidebar schließen falls geöffnet
          const mobileToggleBtn = document.querySelector('button.lg\\:hidden, button[class*="lg:hidden"]') as HTMLButtonElement;
          if (mobileToggleBtn !== null) {
            const isOpen = mobileToggleBtn.querySelector('.fa-bars') === null;
            if (isOpen) {
              mobileToggleBtn.click();
            }
          }
          // Nach oben scrollen, dann Tour weitertreiben
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setTimeout(() => moveNext(), 400);
        }
      } : {})
    }
  },
  ...(!isCommunityMember ? [{
    element: '[data-tour="beratungstermin-btn"]',
    popover: {
      title: 'Strategiegespräch vereinbaren',
      description: 'Du hast noch offene Fragen oder benötigst Unterstützung? Hier kannst Du Dir ein persönlichen Strategiegespräch buchen und herausfinden, wie Du das Beste aus unseren Strategien herausholst!',
      side: getMobileSide('bottom', 'bottom', isMobile),
      align: 'start' as const
    }
  }] : []),
  {
    popover: {
      title: 'Tour abgeschlossen! 🎉',
      description: 'Du kennst jetzt die wichtigsten Bereiche!\n' +
        '\n' +
        'Wir haben Dir nun speziell und kostenfrei eine Umsetzungsanleitung bereitgestellt, wie unsere unterschiedlichen Strategien funktionieren, sodass Du die Tradingideen direkt umsetzen kannst. Gehe dazu zum Reiter „Academy” und schaue Dir die „Börsencampus Umsetzungsanleitung” an!\n' +
        '\n' +
        'Allzeit Gute Trades!',
    }
  }
];

// Für Rückwärtskompatibilität (falls irgendwo direkt importiert)
export const dashboardTourSteps = getDashboardTourSteps(false, true);
